import { useCallback, useState } from "react";
import {
    CircularProgress,
    createStyles,
    Theme,
    Typography,
    WithStyles,
    withStyles
} from "@material-ui/core";
import {
    AddCircle,
    AddPhotoAlternate,
    ArrowDropDownCircle,
    CheckCircle,
    Error,
    NoteAdd,
    RemoveCircle
} from "@material-ui/icons";
import { useDropzone } from "react-dropzone";
import IErrorInfo from "../../models/account/IErrorInfo";
import { FileType } from "../../models/files/FileType";

type IFileUploaderProps = WithStyles<typeof styles> & {
    entityId: string;
    disabled: boolean;
    fileType: FileType;
    fileUploadFn: (files: File[]) => Promise<IErrorInfo[]>;
};

const timeoutError = 4000; // 4 seconds
const timeoutSuccess = 2000; // 2 seconds

const styles = (theme: Theme) =>
    createStyles({
        dropbox: {
            alignItems: "center",
            borderRadius: 10,
            display: "flex",
            justifyContent: "center",
            minHeight: 100,
            padding: theme.spacing(),
            transition: "background-color 0.15s linear",
            width: "100%"
        },
        dropboxDefault: {
            "&:hover": {
                backgroundColor: theme.palette.primary.light,
                border: `2px dashed ${theme.palette.primary.main}`,
                color: theme.palette.primary.contrastText
            },
            backgroundColor: theme.palette.background.default,
            border: `2px dashed ${theme.palette.divider}`,
            cursor: "pointer"
        },
        dropboxDisabled: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary
        },
        dropboxDragHover: {
            backgroundColor: theme.palette.primary.light,
            border: `2px dashed ${theme.palette.primary.main}`,
            color: theme.palette.primary.contrastText
        },
        dropboxError: {
            backgroundColor: theme.palette.error.light,
            border: `1px solid ${theme.palette.error.main}`,
            color: theme.palette.error.contrastText
        },
        dropboxLoading: {
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`
        },
        dropboxSuccess: {
            backgroundColor: theme.palette.success.light,
            border: `1px solid ${theme.palette.success.main}`,
            color: theme.palette.success.contrastText
        },
        errorList: {
            marginBottom: 5,
            marginTop: 5
        },
        errorMessage: { textAlign: "left" },
        iconTextWrapper: { textAlign: "center" }
    });

function FileUploader(props: IFileUploaderProps) {
    // state vars
    const [uploadErrors, setUploadErrors] = useState([] as IErrorInfo[]);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadInProg, setUploadInProg] = useState(false);

    // MIME types, icons, text, and multiple upload based on passed FileType enum.
    const disabled = props.disabled || uploadInProg;
    const [accept, acceptText, icon] = definedAllowedTypes(
        props.fileType,
        disabled
    );
    const [text, multiple] = defineTextAndMultiple(props.fileType, disabled);

    // upload method
    const onDrop = useCallback(
        async (acceptedFiles: File[], rejectedFiles: File[]) => {
            const validationErrors = validateFiles(
                acceptedFiles,
                rejectedFiles,
                multiple,
                acceptText
            );
            if (validationErrors.length > 0) {
                setUploadErrors(validationErrors);
                setTimeout(() => setUploadErrors([]), timeoutError);

                return;
            }

            setUploadInProg(true);
            const errors = await props.fileUploadFn(acceptedFiles);
            setUploadInProg(false);

            if (errors.length > 0) {
                setUploadErrors(errors);
                setTimeout(() => setUploadErrors([]), timeoutError);
            } else {
                setUploadSuccess(true);
                setTimeout(() => setUploadSuccess(false), timeoutSuccess);
            }
        },
        [
            multiple,
            acceptText,
            props.fileUploadFn,
            setUploadErrors,
            setUploadInProg,
            setUploadSuccess
        ]
    );

    // dropzone init
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept,
        disabled,
        onDrop
    });

    // styling + classes
    const classes = props.classes;
    const stateClasses = uploadInProg // in progress
        ? classes.dropboxLoading
        : uploadSuccess // success
          ? classes.dropboxSuccess
          : uploadErrors.length > 0 // errors
            ? classes.dropboxError
            : isDragActive // drag files over
              ? classes.dropboxDragHover
              : disabled // disabled
                ? classes.dropboxDisabled
                : classes.dropboxDefault;

    const dropboxClasses = `${classes.dropbox} ${stateClasses}`;

    return (
        <div {...getRootProps({ className: dropboxClasses })}>
            <input {...getInputProps({ multiple })} />

            <div className={classes.iconTextWrapper}>
                {/* Success */}
                {uploadSuccess && (
                    <>
                        <CheckCircle color="inherit" fontSize="large" />
                        <Typography
                            color="inherit"
                            component="div"
                            variant="body1"
                        >
                            Success
                        </Typography>
                    </>
                )}

                {/* Error */}
                {uploadErrors.length > 0 && (
                    <>
                        <Error color="inherit" fontSize="large" />
                        <Typography
                            color="inherit"
                            component="div"
                            variant="body1"
                        >
                            <ul className={classes.errorList}>
                                {uploadErrors.map((e, idx) => (
                                    <li
                                        className={classes.errorMessage}
                                        key={idx}
                                    >
                                        {e.description}
                                    </li>
                                ))}
                            </ul>
                        </Typography>
                    </>
                )}

                {/* Loading */}
                {uploadInProg && <CircularProgress color="secondary" />}

                {/* Default */}
                {!uploadInProg &&
                    !uploadSuccess &&
                    uploadErrors.length === 0 && (
                        <>
                            {isDragActive ? (
                                <ArrowDropDownCircle fontSize="large" />
                            ) : (
                                icon
                            )}
                            <Typography
                                color="inherit"
                                component="div"
                                variant="body1"
                            >
                                {isDragActive ? "Drop files here!" : text}
                            </Typography>
                        </>
                    )}
            </div>
        </div>
    );
}

const definedAllowedTypes = (
    fileType: FileType,
    disabled: boolean
): [string[] | undefined, string, JSX.Element] => {
    let accept = [] as string[] | undefined;
    let acceptText = "";
    let icon = <AddCircle fontSize="large" />;

    if (disabled) {
        acceptText = "Uploads currently disabled.";
        icon = <RemoveCircle fontSize="large" />;
        return [accept, acceptText, icon];
    }

    switch (fileType) {
        case FileType.AuctionHomePicture:
        case FileType.AuctionPicture:
        case FileType.AuctionMap:
        case FileType.ParcelPicture:
            accept = ["image/*"];
            acceptText = "Only image files can be uploaded here.";
            icon = <AddPhotoAlternate fontSize="large" />;
            break;

        case FileType.ParcelCustom:
            accept = undefined;
            icon = <NoteAdd fontSize="large" />;
            break;

        case FileType.ParcelLandTitle:
        case FileType.ParcelPurchaseAgreement:
            accept = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ];
            acceptText = "Only PDF or word documents can be uploaded here.";
            icon = <NoteAdd fontSize="large" />;
            break;

        default:
            break;
    }

    return [accept, acceptText, icon];
};

const defineTextAndMultiple = (
    fileType: FileType,
    disabled: boolean
): [string, boolean] => {
    let text = "Click or drag files here to upload.";
    let multiple = false;

    if (disabled) {
        text = "Uploads currently disabled.";
        return [text, multiple];
    }

    switch (fileType) {
        case FileType.AuctionPicture:
        case FileType.ParcelPicture:
            multiple = true;
            text =
                "Click or drag files here to upload images for the carousel.";
            break;

        case FileType.AuctionHomePicture:
            text = "Click or drag a file here to upload a home image.";
            break;

        case FileType.AuctionMap:
            text = "Click or drag a file here to upload a map image.";
            break;

        case FileType.ParcelCustom:
            multiple = true;
            text = "Click or drag files here to upload custom files.";
            break;

        case FileType.ParcelLandTitle:
            multiple = true;
            text = "Click or drag a file here to upload a land title.";
            break;

        case FileType.ParcelPurchaseAgreement:
            text = "Click or drag a file here to upload an offer to purchase.";
            break;

        default:
            break;
    }

    return [text, multiple];
};

const validateFiles = (
    acceptedFiles: File[],
    rejectedFiles: File[],
    multiple: boolean,
    acceptText: string
): IErrorInfo[] => {
    const validationErrors = [] as IErrorInfo[];

    // return error if there are any rejects
    if (rejectedFiles.length > 0) {
        validationErrors.push({
            code: "ErrorFileType",
            description: acceptText
        });
    }

    // return error if only one file is allowed
    if (!multiple && acceptedFiles.length > 1) {
        validationErrors.push({
            code: "ErrorMultiple",
            description: "Only one file can be uploaded here."
        });
    }

    // return error if no files were selected (somehow)
    if (acceptedFiles.length === 0 && rejectedFiles.length === 0) {
        validationErrors.push({
            code: "ErrorNoFiles",
            description: "No files were selected for upload."
        });
    }

    return validationErrors;
};

export default withStyles(styles)(FileUploader);
